import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useGetColorThemeQuery } from '../../../services/commonAPI';
import {
  useGetShippingAddressDataQuery,
  useUpdateShippingAddressMutation,
} from "../../../services/myAccountAPI";

function EditShippingAddress() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({});
  const [responseMessage, setResponseMessage] = useState(null);
  const [responseError, setResponseError] = useState(null);
  const [errors, setErrors] = useState({});
 const {
      data: colorthemeData,
    } = useGetColorThemeQuery();
    
  // Fetch Billing Data
  const { data, error, isLoading, refetch } = useGetShippingAddressDataQuery();
  const [updateShippingAddress, { isLoading: isUpdating }] =
    useUpdateShippingAddressMutation();

  // Find the billing address for the user
  const shippingAddress = data?.data?.find(
    (address) => address.user_id.toString() === id
  );

  // Set initial form data when billingAddress is available
  useEffect(() => {
    if (shippingAddress) {
      setFormData(shippingAddress);
    }
  }, [shippingAddress]);

  const handleBack = () => {
    navigate(-1);
  };

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  
    // Clear the error for the specific field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const validateFields = () => {
    const newErrors = {};
  
    if (!formData.first_name?.trim()) newErrors.first_name = "First name is required";
    if (!formData.last_name?.trim()) newErrors.last_name = "Last name is required";
    if (!formData.country?.trim()) newErrors.country = "Country/Region is required";
    if (!formData.street_address?.trim()) newErrors.street_address = "Street address is required";
    if (!formData.town_city?.trim()) newErrors.town_city = "City is required";
    if (!formData.state?.trim()) newErrors.state = "State is required";
  
    if (!formData.pin_code?.trim()) {
      newErrors.pin_code = "Zip Code is required";
    } else if (!/^\d+$/.test(formData.pin_code)) {
      newErrors.pin_code = "Zip Code must contain numbers only";
    }
  
    if (!formData.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{7,15}$/.test(formData.phone)) {
      newErrors.phone = "Phone must be 7-15 digits";
    }
  
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseMessage(null);
    setResponseError(null);

    if (!validateFields()) return;

    if (!shippingAddress?.id) {
      setResponseError("No billing address found for this user.");
      return;
    }

    try {
      const response = await updateShippingAddress({
        shippingId: shippingAddress.id, // Pass ID in the URL, not the body
        body: {
          user_id: id, // Ensure user_id is included in the payload
          ...formData, // Include all other form fields
        },
      }).unwrap();

      if (response?.success) {
        setResponseMessage("Shipping address updated successfully!");
        refetch();
      } else {
        setResponseError(
          response.message || "Failed to edit shipping address."
        );
      }
    } catch (err) {
      console.error("Error updating Shipping address:", err);

      if (err?.data?.message) {
        setResponseError(err.data.message);
      } else {
        setResponseError("An error occurred while saving the address.");
      }
    }
  };

  const handleTextKeyPress = (e) => {
    const regex = /^[a-zA-Z\s]$/;
    if (!regex.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleTextPaste = (field) => (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("Text");
    const cleanedText = pastedText.replace(/[^a-zA-Z\s]/g, "");
    setFormData((prev) => ({ ...prev, [field]: cleanedText }));
  };

  const handleTextKeyPressRemoveSpecialChar = (e) => {
    if (!/^[a-zA-Z0-9\s]$/.test(e.key)) {
      e.preventDefault();
    }
  };
  
  const handleTextPasteRemoveSpecialChar = (field) => (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("Text");
    const cleanedText = pastedText.replace(/[^a-zA-Z0-9\s]/g, "");
    setFormData((prev) => ({ ...prev, [field]: cleanedText }));
  };

  if (isLoading) return <p>Loading page content...</p>;
  if (error) return <p>Error loading page content</p>;

  return (
    <div className="billing-account-form">
      <div className="container">
        <div className="breadcrum text-start">
          <div className="row text-left">
            <div className="col-md-6 col-sm-12 col-lg-6">
              <p onClick={handleBack}>
                <FontAwesomeIcon icon={faAngleLeft} /> Back
              </p>
            </div>
          </div>
        </div>
        <h4>Shipping Address</h4>
        {shippingAddress ? (
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-20">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name || ""}
                    onChange={handleInputChange}
                    
                    onKeyPress={handleTextKeyPress}
                    onPaste={handleTextPaste("first_name")}
                  />
                  {errors.first_name && <small className="text-danger">{errors.first_name}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-20">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name || ""}
                    onChange={handleInputChange}
                   
                    onKeyPress={handleTextKeyPress}
                    onPaste={handleTextPaste("last_name")}
                  />
                  {errors.last_name && <small className="text-danger">{errors.last_name}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-20">
                  <label>Company Name (Optional)</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name || ""}
                    onChange={handleInputChange}
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                  />
                  
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-20">
                  <label>Country/Region *</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country || ""}
                    onChange={handleInputChange}
                 
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                    onPaste={handleTextPasteRemoveSpecialChar("country")}
                  />
                    {errors.country && <small className="text-danger">{errors.country}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-20">
                  <label>Street Address *</label>
                  <textarea
                    rows="4"
                    name="street_address"
                    value={formData.street_address || ""}
                    onChange={handleInputChange}
                    
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                    onPaste={handleTextPasteRemoveSpecialChar("street_address")}
                  />
                    {errors.street_address && <small className="text-danger">{errors.street_address}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-20">
                  <label>City *</label>
                  <input
                    type="text"
                    name="town_city"
                    value={formData.town_city || ""}
                    onChange={handleInputChange}
                  
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                    onPaste={handleTextPasteRemoveSpecialChar("town_city")}
                  />
                   {errors.town_city && <small className="text-danger">{errors.town_city}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-20">
                  <label>State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state || ""}
                    onChange={handleInputChange}
                    
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                    onPaste={handleTextPasteRemoveSpecialChar("state")}
                  />
                   {errors.state && <small className="text-danger">{errors.state}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-20">
                  <label>Zip Code *</label>
                  <input
                    type="text"
                    name="pin_code"
                    value={formData.pin_code || ""}
                    onChange={handleInputChange}
                   
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                    onPaste={handleTextPasteRemoveSpecialChar("pin_code")}
                  />
                    {errors.pin_code && <small className="text-danger">{errors.pin_code}</small>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-20">
                  <label>Phone *</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone || ""}
                    onChange={handleInputChange}
                    
                    onKeyPress={handleTextKeyPressRemoveSpecialChar}
                    onPaste={handleTextPasteRemoveSpecialChar("phone")}
                  />
                   {errors.phone && <small className="text-danger">{errors.phone}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-20">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ""}
                    onChange={handleInputChange}
                   
                  />
                   {errors.email && <small className="text-danger">{errors.email}</small>}
                </div>
              </div>
              <div className="col-md-12">
                <button
                  type="submit"
                  className="primary-button"
                  disabled={isUpdating}
                  style={{ backgroundColor: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }}
                >
                  {isUpdating ? "Saving..." : "Save Address"}
                </button>
              </div>
              {responseMessage && (
                <p className="success-message">{responseMessage}</p>
              )}
              {responseError && (
                <p className="error-message">{responseError}</p>
              )}
            </div>
          </form>
        ) : (
          <p>No Shipping address found for this user.</p>
        )}
      </div>
    </div>
  );
}

export default EditShippingAddress;
